import { formatDimensionValue } from "@/lib/formatDimensionValue";

import type {
  ClarificationField,
  ClarificationFilterKey,
  PendingClarification,
} from "./kaiTypes";

export interface ClarificationSummaryItem {
  label?: string;
  value: string;
}

export function hasActiveClarification(
  clarifications: PendingClarification[],
): boolean {
  return clarifications.some(
    (c) => c.status === "pending" || c.status === "submitting",
  );
}

type RawOption = string | { value?: string; label?: string };

interface RawClarificationField {
  id?: string;
  name?: string;
  label?: string;
  type?: string;
  options?: RawOption[];
  filter_key?: string;
  placeholder?: string;
  required?: boolean;
  conditional?: { field: string; value: string };
}

const FILTER_KEYS = new Set<ClarificationFilterKey>([
  "sales_person",
  "agent",
  "shipper",
  "consignee",
  "opened_by",
]);

function normalizeOption(
  opt: RawOption,
): { value: string; label: string } | null {
  if (typeof opt === "string") {
    return { value: opt, label: opt };
  }
  const value = opt.value ?? opt.label;
  if (!value) return null;
  return { value, label: opt.label ?? value };
}

function asFieldType(value: string | undefined): ClarificationField["type"] {
  if (
    value === "radio_tags" ||
    value === "text" ||
    value === "date" ||
    value === "port_select" ||
    value === "booking_filter_select"
  ) {
    return value;
  }
  return "text";
}

/**
 * Coerce streamed clarification fields into the form renderer shape.
 * Maps `name` → `id`, coerces string options, and drops empty radio groups.
 */
export function normalizeClarificationFields(
  fields: unknown,
): ClarificationField[] {
  if (!Array.isArray(fields)) return [];

  const result: ClarificationField[] = [];
  for (const raw of fields as RawClarificationField[]) {
    if (!raw || typeof raw !== "object") continue;

    const id = raw.id ?? raw.name;
    if (!id) continue;

    let type = asFieldType(raw.type);
    const options = Array.isArray(raw.options)
      ? raw.options
          .map(normalizeOption)
          .filter((o): o is { value: string; label: string } => o !== null)
      : undefined;

    const rawFilterKey = raw.filter_key ?? id;
    const entityKey = FILTER_KEYS.has(rawFilterKey as ClarificationFilterKey)
      ? (rawFilterKey as ClarificationFilterKey)
      : undefined;

    // Mirror the backend repair: an entity picker emitted as an empty
    // `radio_tags` (the model can't enumerate parties/people inline) becomes a
    // `booking_filter_select` autosuggest keyed by that dimension. Otherwise an
    // options-less radio group renders as broken pills, so drop it.
    if (type === "radio_tags" && (!options || options.length === 0)) {
      if (!entityKey) continue;
      type = "booking_filter_select";
    }

    const filterKey =
      type === "booking_filter_select"
        ? entityKey
        : raw.filter_key &&
            FILTER_KEYS.has(raw.filter_key as ClarificationFilterKey)
          ? (raw.filter_key as ClarificationFilterKey)
          : undefined;

    result.push({
      id,
      label: raw.label ?? id,
      type,
      options: type === "booking_filter_select" ? undefined : options,
      filter_key: filterKey,
      placeholder: raw.placeholder,
      required: raw.required,
      conditional: raw.conditional,
    });
  }
  return result;
}

export function isFieldRequired(field: ClarificationField): boolean {
  return field.required !== false;
}

export function isFieldVisible(
  field: ClarificationField,
  answers: Record<string, string>,
): boolean {
  if (!field.conditional) return true;
  return answers[field.conditional.field] === field.conditional.value;
}

/** Render a `YYYY-MM-DD` answer as e.g. "Jul 1, 2026"; pass through anything else. */
function formatIsoDateLabel(raw: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const [year, month, day] = raw.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Human-readable label for a stored clarification answer. */
export function formatClarificationAnswer(
  field: ClarificationField,
  raw: string,
): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  if (field.type === "radio_tags") {
    return field.options?.find((o) => o.value === trimmed)?.label ?? trimmed;
  }
  if (field.type === "date") {
    return formatIsoDateLabel(trimmed);
  }
  if (field.filter_key) {
    return formatDimensionValue(field.filter_key, trimmed);
  }
  return trimmed;
}

/**
 * Build compact summary rows for a resolved clarification. Ignores conditional
 * visibility so submitted answers still render after the form collapses.
 */
export function buildClarificationSummaryItems(
  fields: ClarificationField[],
  answers: Record<string, string>,
  title?: string,
): ClarificationSummaryItem[] {
  const items: ClarificationSummaryItem[] = [];
  const usedKeys = new Set<string>();
  const normalizedTitle = title?.trim().toLowerCase();

  for (const field of fields) {
    const value = formatClarificationAnswer(field, answers[field.id] ?? "");
    if (!value) continue;
    usedKeys.add(field.id);

    const labelMatchesTitle =
      normalizedTitle != null &&
      field.label.trim().toLowerCase() === normalizedTitle;
    const omitLabel = fields.length === 1 || labelMatchesTitle;

    items.push({
      label: omitLabel ? undefined : field.label,
      value,
    });
  }

  for (const [key, raw] of Object.entries(answers)) {
    if (usedKeys.has(key)) continue;
    const value = raw.trim();
    if (!value) continue;
    items.push({ label: key, value });
  }

  return items;
}
